import React from "react";
import Logo from "./Logo";

const CreditTermsPage = () => (
  <div className="min-h-screen bg-slate-950 text-slate-100 font-sans">
    <header className="fixed w-full z-50 bg-slate-950/95 backdrop-blur-md shadow-lg border-b border-slate-800 py-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
          <Logo />
        </a>
        <div className="flex items-center gap-3">
          <a
            href="/terms"
            className="text-sm text-slate-400 hover:text-slate-200 transition-colors"
          >
            Terms of Use
          </a>
          <a
            href="/privacy"
            className="text-sm text-sky-400 hover:text-sky-300 transition-colors border border-sky-500/40 rounded-lg px-3 py-1.5"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </header>

    <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-10 space-y-10">
      <div>
        <p className="text-sky-400 uppercase text-xs font-semibold tracking-widest mb-3">Supplemental Terms</p>
        <h1 className="text-4xl font-bold text-white mb-4">Supplemental Credit Terms</h1>
        <p className="text-slate-400">
          Effective January 1, 2025
        </p>
        <p className="text-slate-400 mt-4">
          These Supplemental Credits Terms ("Credit Terms") form part of the agreement you or the organization,
          company, or other entity that you represent ("Customer") have with CloudMortgage.io that links to or
          otherwise references these Credit Terms ("Agreement"). Any capitalized terms not defined in these
          Credit Terms will have the definitions stated in the Agreement.
        </p>
      </div>

      <section className="space-y-6">
        <h2 className="text-2xl font-bold text-white">A. Types of Credits</h2>

        <div className="space-y-4">
          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h3 className="text-lg font-semibold text-sky-400 mb-3">Usage Credits</h3>
            <p className="text-slate-400">
              Unless otherwise agreed to between Customer and CloudMortgage.io, CloudMortgage.io may require
              Customer to prepay for certain Services by purchasing credits sufficient to cover Customer's usage
              according to our Pricing Page, as may be updated from time to time ("Usage Credits"). Customer's
              order for Usage Credits constitutes an offer by Customer to purchase those Usage Credits.
              CloudMortgage.io will notify Customer if it accepts Customer's offer (the "Confirmation Notice").
              These Credit Terms will become effective when CloudMortgage.io sends the Confirmation Notice.
            </p>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h3 className="text-lg font-semibold text-sky-400 mb-3">Promotional Credits</h3>
            <p className="text-slate-400">
              CloudMortgage.io may, in its sole discretion, offer Customer free credits as part of a promotion
              or during sign up for the Services ("Promotional Credits").
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-bold text-white">B. Terms</h2>

        <div className="space-y-4">
          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h3 className="text-lg font-semibold text-sky-400 mb-3">Non-Refundable</h3>
            <p className="text-slate-400">
              Credits issued by CloudMortgage.io, which includes Usage Credits, Promotional Credits and any
              other credits CloudMortgage.io offers ("Credits") are non-refundable. Usage Credits expire one
              calendar year from the date CloudMortgage.io sends the Confirmation Notice or otherwise issues
              the Credits. Promotional Credits expire at the time indicated when issued (or otherwise one
              calendar year from when issued if no time is specified). Credits will also automatically expire
              if Customer closes its CloudMortgage.io account and cannot be recovered. Credits are not legal
              tender or currency of any kind and have no cash value. Customer can check the number of Credits
              associated with Customer's CloudMortgage.io account on the Plans & Billing Page within the
              Account Settings.
            </p>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h3 className="text-lg font-semibold text-sky-400 mb-3">No Transfer</h3>
            <p className="text-slate-400">
              Customer may not transfer or sell Credits, whether paid for or provided as part of a promotion,
              to any other person or entity, and Credits may only be used by the holder of the CloudMortgage.io
              account to which the Credits are associated.
            </p>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
            <h3 className="text-lg font-semibold text-sky-400 mb-3">Balance Maintenance Service</h3>
            <p className="text-slate-400">
              Customer may have the option to automatically reload Credits by a certain amount ("Balance
              Maintenance Service") when Customer reaches a certain level, which may be set by Customer on
              the Plans & Billing Page of Customer's CloudMortgage.io account. If Customer activates the
              Balance Maintenance Service, Customer authorizes CloudMortgage.io to charge the payment method
              designated in Customer's account on a recurring basis for the applicable fees and taxes for the
              amount of Credits added to Customer's CloudMortgage.io account as part of the Balance Maintenance
              Service, until Customer opts out of the Balance Maintenance Service. All Credits added to
              Customer's CloudMortgage.io account as part of the Balance Maintenance Service will be charged
              on or around the day on which the Credits are added to Customer's CloudMortgage.io account.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-white">Credit Usage for AI Services</h2>
        <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-800">
          <p className="text-slate-400 mb-4">
            Credits are consumed when using AI-powered features within CloudMortgage.io, including but not limited to:
          </p>
          <ul className="list-disc list-inside text-slate-400 space-y-2">
            <li>AI Case Assistant conversations and document analysis</li>
            <li>Marketing Assistant content generation</li>
            <li>Design Assistant image creation</li>
            <li>Video Assistant video generation</li>
            <li>Meeting Assistant transcription and summaries</li>
            <li>Automated document processing and extraction</li>
          </ul>
          <p className="text-slate-400 mt-4">
            Credit consumption rates vary based on the complexity and type of AI operation performed.
            Detailed credit usage information is available in your account dashboard.
          </p>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-2xl font-bold text-white">Questions</h2>
        <p className="text-slate-400">
          For questions about Credits or these Credit Terms, please contact us at{" "}
          <a href="mailto:support@cloudmortgage.io" className="text-sky-400 hover:text-sky-300 transition-colors">
            support@cloudmortgage.io
          </a>.
        </p>
      </section>

      <div className="pt-6">
        <a
          href="/"
          className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
        >
          Back to Home
        </a>
      </div>
    </main>

    <footer className="bg-black text-slate-500 py-8 border-t border-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm mb-4">
          <a href="/terms" className="text-slate-400 hover:text-slate-200 transition-colors">
            Terms
          </a>
          <a href="/privacy" className="text-slate-400 hover:text-slate-200 transition-colors">
            Privacy
          </a>
          <a href="/credit-terms" className="text-slate-400 hover:text-slate-200 transition-colors">
            Credit Terms
          </a>
        </div>
        <p>© 2026 CloudMortgage.io. All rights reserved.</p>
      </div>
    </footer>
  </div>
);

export default CreditTermsPage;
